import Kichiri from "./KichiriService";
import UserService from "./UserService";
import { authedFetch, readJsonAnswer, readKichiriError, COULD_NOT_REACH_THE_SERVER } from "./apiFetch";
import UtilityService from "./UtilityService";

const JaneReportService = {
	/**
	 * Uploads a month of Jane CSV exports for a client and returns the figures a journal is built from,
	 * where each one was read from, and the reconciliation between them on the client's revenue basis
	 * for that month. Kichiri does not support multipart, so the upload goes through authedFetch, which
	 * carries the login and sends an expired one to the login page like every other call.
	 *
	 * @param {Object} params
	 * @param {number} params.clientId
	 * @param {Array<File>} params.files
	 * @returns {Promise<Object>} the extraction, or { error }
	 */
	async extractReports({ clientId, files }) {
		const formData = new FormData();
		formData.append("clientId", clientId);
		for (const file of files) {
			formData.append("files", file, file.name);
		}

		try {
			const response = await authedFetch("/tools/jane_reports/extract", { method: "POST", body: formData });
			const answer = await readJsonAnswer({ response });
			return answer;
		} catch (error) {
			console.error("Error extracting Jane reports:", error);
			return { error: COULD_NOT_REACH_THE_SERVER };
		}
	},

	/**
	 * Lists the client's QuickBooks chart of accounts for the mapping screen.
	 *
	 * @param {Object} params
	 * @param {number} params.clientId
	 * @returns {Promise<Object>}
	 */
	async listQuickBooksAccounts({ clientId }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.janeReportsQuickBooksAccounts({ clientId }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			console.error("Error listing QuickBooks accounts:", error);
			return { error: readKichiriError({ error }) };
		}
	},

	/**
	 * Lists the client's QuickBooks sales tax codes, with a proposal for which one means taxable and
	 * which means exempt. The rate the Jane reports themselves imply is sent along so the proposal can
	 * say whether the two agree.
	 *
	 * @param {Object} params
	 * @param {number} params.clientId
	 * @param {number} params.impliedRatePercent
	 * @returns {Promise<Object>}
	 */
	async listQuickBooksTaxCodes({ clientId, impliedRatePercent }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.janeReportsQuickBooksTaxCodes({ clientId, impliedRatePercent }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			console.error("Error listing QuickBooks tax codes:", error);
			return { error: readKichiriError({ error }) };
		}
	},

	/**
	 * Lists every item the period sold with the revenue account it is set to credit, plus the accounts
	 * available to choose from.
	 *
	 * @param {Object} params
	 * @param {number} params.clientId
	 * @param {Array} params.soldItems
	 * @returns {Promise<Object>}
	 */
	async listItemHeads({ clientId, soldItems }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.janeReportsItemHeads({ clientId, soldItems }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			console.error("Error reading item revenue accounts:", error);
			return { error: readKichiriError({ error }) };
		}
	},

	/**
	 * Asks an AI which revenue account each unmapped item belongs to. Answers come back as proposals a
	 * person still has to agree with.
	 *
	 * @param {Object} params
	 * @param {number} params.clientId
	 * @param {Array} params.soldItems
	 * @returns {Promise<Object>}
	 */
	async proposeItemHeads({ clientId, soldItems }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.janeReportsItemHeadsPropose({ clientId, soldItems }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			console.error("Error proposing item revenue accounts:", error);
			return { error: readKichiriError({ error }) };
		}
	},

	/**
	 * Records the preparer's decision about which revenue account one or more items credit.
	 *
	 * @param {Object} params
	 * @param {number} params.clientId
	 * @param {Array} params.decisions
	 * @returns {Promise<Object>}
	 */
	async confirmItemHeads({ clientId, decisions }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.janeReportsItemHeadsConfirm({ clientId, decisions }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			console.error("Error confirming item revenue accounts:", error);
			return { error: readKichiriError({ error }) };
		}
	},

	/**
	 * Lists the sites this period sold from as pairing rows: each with the QuickBooks location or class it
	 * is paired with — whichever the client's site setting records it as — and a proposed pairing where
	 * none has been made.
	 *
	 * @param {Object} params
	 * @param {number} params.clientId
	 * @param {Array} params.janeLocations
	 * @param {string} params.target - "qboLocation" or "qboClass"
	 * @returns {Promise<Object>} { rows, options } or { error }
	 */
	async listSitePairings({ clientId, janeLocations, target }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.janeReportsSitePairings({ clientId, janeLocations, target }, {}, userData.authToken);
			const { sites, quickBooksOptions } = response.data;

			const rows = sites.map(site => {
				const hasSuggestion = Boolean(site.suggestion && site.suggestion.qboReferenceId);
				return {
					key: site.janeName,
					label: site.janeName,
					sublabel: "as Jane names it",
					amount: UtilityService.formatMoney({ value: site.grossAmount, emptyText: "—" }),
					isRequired: true,
					chosenId: site.qboReferenceId,
					suggestion: hasSuggestion
						? { id: site.suggestion.qboReferenceId, name: site.suggestion.qboReferenceName, evidence: site.suggestion.evidence, isCertain: site.suggestion.isCertain }
						: null
				};
			});
			const options = quickBooksOptions.map(option => ({ id: option.id, name: option.name }));

			return { rows, options };
		} catch (error) {
			console.error("Error reading site pairings:", error);
			return { error: readKichiriError({ error }) };
		}
	},

	/**
	 * Saves which QuickBooks location or class each of Jane's site names means, from pairing rows.
	 *
	 * @param {Object} params
	 * @param {number} params.clientId
	 * @param {string} params.target - "qboLocation" or "qboClass"
	 * @param {Array} params.pairings - [{ row, id, name }]
	 * @returns {Promise<Object>}
	 */
	async saveSitePairings({ clientId, target, pairings }) {
		let userData = UserService.getUserData();
		const sitePairings = pairings.map(pairing => ({ janeName: pairing.row.key, qboReferenceId: pairing.id, qboReferenceName: pairing.name }));

		try {
			let response = await Kichiri.tools.janeReportsSitePairingsUpdate({ clientId, target, pairings: sitePairings }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			console.error("Error saving site pairings:", error);
			return { error: readKichiriError({ error }) };
		}
	},

	/**
	 * Reads this client's journal settings for a month — the revenue basis, what fills the revenue
	 * account, the location and the class, and the receivable — each with who decided it and when, and a
	 * proposal for anything nobody has decided.
	 *
	 * @param {Object} params
	 * @param {number} params.clientId
	 * @param {string} params.periodStart - the month being built, or omitted for today
	 * @returns {Promise<Object>} { settings, proposal, needsReasonWhenNone, needsAMonth, reportsNeeded, startingPoints } or { error }
	 */
	async readJournalShape({ clientId, periodStart }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.janeReportsJournalShape({ clientId, periodStart }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			console.error("Error reading the journal settings:", error);
			return { error: readKichiriError({ error }) };
		}
	},

	/**
	 * Saves this client's journal settings.
	 *
	 * @param {Object} params
	 * @param {number} params.clientId
	 * @param {Object} params.settings - { basis?, account?, location?, class?, receivable? }, each a source or null
	 * @param {Object} params.reasons - setting → why, for a "none" the client's own journals contradict
	 * @param {string} params.effectiveFrom - the first day of the month a dated setting starts in
	 * @param {string} params.periodStart - the month the panel shows, so the answer is read for it
	 * @returns {Promise<Object>} the settings as now saved, or { error }
	 */
	async saveJournalShape({ clientId, settings, reasons, effectiveFrom, periodStart }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.janeReportsJournalShapeUpdate({ clientId, settings, reasons, effectiveFrom, periodStart }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			console.error("Error saving the journal settings:", error);
			return { error: readKichiriError({ error }) };
		}
	},

	/**
	 * Compares the built journal against the last one this client posted and returns every way the two
	 * differ. Read only — it changes nothing.
	 *
	 * @param {Object} params
	 * @param {number} params.clientId
	 * @param {Object} params.built
	 * @param {string} params.periodEnd
	 * @returns {Promise<Object>}
	 */
	async compareWithPreviousJournal({ clientId, built, periodEnd }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.janeReportsComparePrevious({ clientId, built, periodEnd }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			console.error("Error comparing with the previous journal:", error);
			return { error: readKichiriError({ error }) };
		}
	},

	/**
	 * Reads which QuickBooks account each journal role posts to for this client.
	 *
	 * @param {Object} params
	 * @param {number} params.clientId
	 * @returns {Promise<Object>}
	 */
	async listAccountMappings({ clientId }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.janeReportsAccountMappings({ clientId }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			console.error("Error reading account mappings:", error);
			return { error: readKichiriError({ error }) };
		}
	},

	/**
	 * Proposes an account for each journal role from this client's previous Jane journals.
	 *
	 * @param {Object} params
	 * @param {number} params.clientId
	 * @returns {Promise<Object>}
	 */
	async suggestAccountMappings({ clientId, figures }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.janeReportsSuggestAccountMappings({ clientId, figures }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			console.error("Error suggesting account mappings:", error);
			return { error: readKichiriError({ error }) };
		}
	},

	/**
	 * Saves which QuickBooks account each journal role posts to for this client.
	 *
	 * @param {Object} params
	 * @param {number} params.clientId
	 * @param {Array} params.mappings
	 * @returns {Promise<Object>}
	 */
	async saveAccountMappings({ clientId, mappings }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.janeReportsAccountMappingsUpdate({ clientId, mappings }, {}, userData.authToken);
			return response.data;
		} catch (error) {
			console.error("Error saving account mappings:", error);
			return { error: readKichiriError({ error }) };
		}
	},

	/**
	 * Builds the journal from extracted figures and this client's saved account mapping.
	 *
	 * @param {Object} params
	 * @param {number} params.clientId
	 * @param {Object} params.figures
	 * @returns {Promise<Object>}
	 */
	async buildJournal({ clientId, figures, priorPeriodReceivable, receivableAsAt }) {
		let userData = UserService.getUserData();

		try {
			let response = await Kichiri.tools.janeReportsBuildJournal(
				{ clientId, figures, priorPeriodReceivable, receivableAsAt },
				{},
				userData.authToken
			);
			return response.data;
		} catch (error) {
			console.error("Error building the journal:", error);
			return { error: readKichiriError({ error }) };
		}
	}
};

export default JaneReportService;
